import { VStack, Divider, Box, Heading, HStack, Button, SimpleGrid } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { LucidePencil } from 'lucide-react';
import { useMemo, useCallback } from 'react';
import { IUseStepState } from '@duke-hq/ui/components/StepWizard/IUseStepState';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { normalisePostcodeForDisplay } from '@duke-hq/google-address/services/transform/postcode';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { getUserFullName } from '@duke-hq/lib';
import { useAuthTenant } from '../useAuthTenant';
import { StudentData } from '@duke-hq/svc-api-db';
import { getGenderOptions, getDisabilitiesOptions, getSchoolLevelOptions } from './constants';
import { djs } from '@duke-hq/djs';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

interface SectionHeadingProps {
  step?: number;
  children: React.ReactNode;
  onEdit?: () => void;
  editDetails?: boolean;
}

export function SectionHeading({ children, onEdit, editDetails }: SectionHeadingProps) {
  return (
    <HStack justifyContent="space-between">
      <Heading fontSize="lg" size="xs">
        {children}
      </Heading>
      {editDetails === true ? (
        <Button leftIcon={<LucidePencil size="14" />} variant="ghost" onClick={onEdit}>
          <Trans>Edit</Trans>
        </Button>
      ) : null}
    </HStack>
  );
}

export function BasicInformation({
  student,
  studentData,
}: {
  student: NonNullable<RouterOutputs['users']['studentById']>;
  studentData: StudentData;
}) {
  return (
    <SimpleGrid columns={2}>
      <SingleField name={<Trans>First Name</Trans>} value={student?.first_name} />
      <SingleField name={<Trans>Last name</Trans>} value={student?.last_name} />
      <SingleField name={<Trans>Email address</Trans>} value={student?.email} />
      <SingleField name={<Trans>Phone number</Trans>} value={student?.phone_number} />
      <SingleField name={<Trans>USI</Trans>} value={studentData?.universal_student_identifier} />
    </SimpleGrid>
  );
}

export function AddressSection({
  studentData,
  postcodeWarning,
}: {
  studentData: StudentData;
  postcodeWarning?: string | null;
}) {
  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }}>
      <SingleField
        name={<Trans>Street</Trans>}
        value={
          [studentData?.addressStreetNumber, studentData?.addressStreetName]
            .filter(Boolean)
            .join(' ') || undefined
        }
      />
      <SingleField name={<Trans>City/Town/Suburb</Trans>} value={studentData.addressLocality} />
      <SingleField
        name={<Trans>Unit/Suite/Building</Trans>}
        value={studentData.addressStreetSubpremise}
      />
      <SingleField name={<Trans>State</Trans>} value={studentData?.addressState} />
      <SingleField
        name={<Trans>Postcode</Trans>}
        value={normalisePostcodeForDisplay(studentData.addressPostcode)}
        warning={postcodeWarning}
      />
    </SimpleGrid>
  );
}

export function AdditionalInfoSection({ studentData }: { studentData: StudentData }) {
  const genderOptions = getGenderOptions();
  const schoolOptions = getSchoolLevelOptions();
  const disabilityOptions = getDisabilitiesOptions();

  return (
    <SimpleGrid columns={2}>
      <SingleField
        name={<Trans>Date of birth</Trans>}
        value={
          studentData?.date_of_birth ? djs(studentData.date_of_birth).format('DD MMMM YYYY') : ''
        }
      />
      <SingleField
        name={<Trans>Gender</Trans>}
        value={genderOptions.find((option) => option.value === studentData?.gender)?.label ?? ''}
      />
      <SingleField
        name={<Trans>Highest completed school level</Trans>}
        value={
          schoolOptions.find((option) => option.value === studentData?.highest_education)?.label ??
          ''
        }
      />
      <SingleField
        name={<Trans>Disability and special assistance</Trans>}
        value={
          disabilityOptions
            .filter((option) => studentData?.disabilities?.includes(option.value))
            .map((option) => option.label) ?? ''
        }
      />
    </SimpleGrid>
  );
}

function DetailsSection({
  studentData,
  ownerOptions,
}: {
  studentData: StudentData;
  ownerOptions: { value: string; label: string }[];
}) {
  return (
    <SimpleGrid columns={1}>
      <HStack justify={'space-between'}>
        <SingleField
          name={<Trans>Owner</Trans>}
          value={ownerOptions?.find((option) => option.value === studentData.owned_by)?.label ?? ''}
        />
      </HStack>
    </SimpleGrid>
  );
}

export function StudentUserDetail(props: {
  stepState?: IUseStepState;
  student: NonNullable<RouterOutputs['users']['studentById']> | null;
  isEditMode?: boolean;
}) {
  const { student, stepState, isEditMode = false } = props;
  const auth = useAuthTenant();

  const consultantsQuery = trpcTenant.users.allUsersAtAvailableSiteLocations.useQuery();

  const ownerOptions = useMemo(() => {
    return (
      consultantsQuery.data?.map((consultant) => ({
        value: consultant.id,
        label: `${getUserFullName(consultant)} ${auth.user.id === consultant.id ? `(${t`myself`})` : ''}`,
      })) ?? []
    );
  }, [consultantsQuery.data, auth.user.id]);

  const handleEdit = useCallback(
    (step: number) => {
      stepState?.setStep(step);
    },
    [stepState],
  );

  return (
    <Box p={4}>
      <VStack spacing={4} gap={0} align="stretch">
        <Box>
          <SectionHeading step={0} onEdit={() => handleEdit(0)} editDetails={isEditMode}>
            <Trans>Basic Information</Trans>
          </SectionHeading>
          {student?.studentData ? (
            <BasicInformation student={student} studentData={student?.studentData} />
          ) : null}
        </Box>

        <Divider my={2} />

        <Box>
          <SectionHeading step={1} onEdit={() => handleEdit(1)} editDetails={isEditMode}>
            <Trans>Address</Trans>
          </SectionHeading>
          {student?.studentData ? (
            <AddressSection
              studentData={student.studentData}
              postcodeWarning={student.postcodeWarnings?.addressWarning}
            />
          ) : null}
        </Box>

        <Divider my={2} />

        <Box>
          <SectionHeading step={2} onEdit={() => handleEdit(2)} editDetails={isEditMode}>
            <Trans>Additional Info</Trans>
          </SectionHeading>
          {student?.studentData ? (
            <AdditionalInfoSection studentData={student.studentData} />
          ) : null}
        </Box>

        <Divider my={2} />

        <Box>
          <SectionHeading step={3} onEdit={() => stepState?.setStep(3)} editDetails={isEditMode}>
            <Trans>Details</Trans>
          </SectionHeading>
          {student?.studentData ? (
            <DetailsSection studentData={student.studentData} ownerOptions={ownerOptions} />
          ) : null}
        </Box>
      </VStack>
    </Box>
  );
}
